import { PhoneNumberE164, User, UserSettingsField } from "@sp-crm/core";
import { WhiteLabelRestricted } from "components/app/white-label-restricted";
import { ContentFull, ContentHeader } from "components/layout";
import { PhoneInputE164 } from "components/shared/phone-input-e164";
import { AutosavingInput } from "components/ui/autosaving-input";
import { defaultCardStyle } from "components/ui/card";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { InternalLink } from "components/ui/internal-link";
import { useGetCurrentUserQuery } from "generated/graphql";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateUser } from "store/actions";
import { useTenantSettings } from "store/selectors/settings";
import { ApplicationState } from "store/state";
import { UserProfileSettings } from "./user-profile-settings";

type TwoFactorStatus = "loading" | "enabled" | "disabled";

export const AccountSettingsPersonal: React.FC<unknown> = () => {
    const user = useSelector(
        (state: ApplicationState) => state.users.users[state.userState.userId],
    );
    const currentUserQuery = useGetCurrentUserQuery({}, { keepPreviousData: true });
    const dispatch = useDispatch();
    const commitUserValue = React.useCallback(
        (field: UserSettingsField | string, newValue: string) => {
            const commonUser = User.load(user);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (commonUser as any)[field] = newValue;
            updateUser(commonUser, dispatch);
        },
        [dispatch, user],
    );
    const publicProfileEnabled = useTenantSettings().settings.enablePublicProfile;

    const sms2faStatus: TwoFactorStatus = useMemo(() => {
        if (!currentUserQuery.data?.getCurrentUser) {
            return "loading";
        }

        return currentUserQuery.data.getCurrentUser.twoFactorSmsEnabled
            ? "enabled"
            : "disabled";
    }, [currentUserQuery.data]);

    const handlePhoneChange = React.useCallback(
        (newValue: PhoneNumberE164) => {
            commitUserValue("phone", newValue);
        },
        [commitUserValue],
    );

    return (
        <div className="space-y-4 lg:space-y-8">
            <ContentFull>
                <ContentHeader>Personal information</ContentHeader>
                <div className="space-y-4">
                    <AutosavingInput
                        label="Display name"
                        initial={user.name}
                        onCommit={(newValue: string) => commitUserValue("name", newValue)}
                    />
                    {sms2faStatus === "loading" ? null : (
                        <div>
                            <PhoneInputE164
                                label="Phone number"
                                initial={user.phone}
                                disabled={sms2faStatus === "enabled"}
                                onCommit={handlePhoneChange}
                                showButtons={false}
                            />
                            {sms2faStatus === "enabled" && (
                                <div className="mt-2">
                                    <InlineBanner type="info">
                                        To change your phone number, first disable SMS
                                        two-factor authentication in your{" "}
                                        <InternalLink
                                            to="/settings/account/2fa"
                                            className="underline">
                                            two-factor authentication settings
                                        </InternalLink>
                                        .
                                    </InlineBanner>
                                </div>
                            )}
                        </div>
                    )}
                    <Input label="Login email" disabled value={user.email} />
                    <div className="group">
                        <AutosavingInput
                            label="Display email"
                            initial={user.displayEmail}
                            onCommit={(newValue: string) =>
                                commitUserValue("displayEmail", newValue)
                            }
                            onEmpty={() => user.email}
                        />
                        <p className="ml-6 mt-1 text-gray-500 text-sm group-hover:text-gray-900">
                            Specify an email to display on shared documents (e.g. client
                            referrals and community comparison documents) instead of using
                            your login email.
                        </p>
                    </div>
                </div>
            </ContentFull>
            {publicProfileEnabled ? (
                <WhiteLabelRestricted>
                    <div className={defaultCardStyle}>
                        <UserProfileSettings />
                    </div>
                </WhiteLabelRestricted>
            ) : null}
        </div>
    );
};
